import { executeStudioCommand, executeEstimatedAnalysis, rememberStudioEstimate } from '../commands/commandTransport';
import type { DossierThread } from './threadContracts';
import { studioRequestId } from '../commands/requestId';
import type {RelationsInput,RelationsResult} from './relationsContracts';
import { restFaithfulRepository } from '@/uptk/core/components/moduleEntity/faithful/repositories';
import type { GapReport } from '../catalog/gapReport';
import type {ZoneCodeInput,ZoneCodeResult} from '../designer/zoneExtensions';
import type { BindingCandidate } from '../designer/assistedBindings';
import type { ModuleSpecBlock as BindingBlock } from './moduleSpecContracts';
import type { PrototypeConversionInput, PrototypeConversionEstimate, PrototypeConversionJob } from './conversionContracts';
import type { TranslationPreference, StarterTestPlanResult } from './cleanModelContracts';
import type { ModuleComponents } from './contracts';
import type { ScreenAnalysis, ScreenAnalysisInput } from './screenAnalysisContracts';
import type { CapabilityCatalog, CoverageReport, NavigationProposal } from './contracts';
import type { ReferenceCapture, ReferenceCaptureInput } from './contracts';
import type { ModuleSpecTranslationEstimate, ModuleSpecTranslationJob } from './contracts';
import { SimulationSpecErrorsError } from './ModuleStudioRepository';
import type { Simulation, SimulationSummary, SimulationCreateInput, SimulationUpdateInput, SimulationGenerateInput, SimulationRefreshInput, SimulationRefreshResult, SimulationAcknowledgeInput, SimulationShareInput, SimulationShareCreated, GeneratorIssue } from './contracts';
import type { Idea, IdeaInput, IdeaEstimate, IdeaTransfer, IdeaTransferInput, IdeaRepositoryTree } from './contracts';
/**
 * Module Studio (UPT-2079 lots L0 à L5, UPT-2096 P1-a / P1-b, UPT-2095) — dépôt REST `/api/v1/module-studio/…`
 * et `/api/v1/module-spec/…` (versions de Module Spec, UPT-2095 §11.2 ; brouillon du Concepteur, export et catalogue, L3 §4.2).
 *
 * Client HTTP du socle (`BaseApi` : jeton, tenantId, workspaceId injectés par
 * les en-têtes SaaS comme les autres modules). Routes = contrat des lots
 * backend (web/SPEC-UPT-2079-L0.md §4.4), respecté dès maintenant.
 *
 * En L0 aucune ressource n'est servie : le fournisseur ne route jamais vers
 * cette implémentation. Une méthode appelée malgré tout lève `NotServedError`.
 */
import BaseApi from '@/uptk/core/api/BaseApi';
import type {
  DeliveryCheck,
  DesignDossier,
  DesignDossierInput,
  DesignModuleSpecInput,
  DesignModuleSpecPayload,
  DesignSpecDetail,
  DesignSpecDiff,
  DesignSpecDocumentInput,
  DesignSpecGenerateInput,
  DesignSpecSyncInput,
  DesignSpecSyncResult,
  DesignSpecVersion,
  DesignSpecVersionSummary,
  DesignerCatalog,
  EnvironmentCode,
  Feedback,
  FeedbackClassifyInput,
  FeedbackFilter,
  FeedbackInput,
  FeedbackPage,
  FeedbackRejectInput,
  FeedbackReopenInput,
  FeedbackResolveInput,
  FeedbackSummary,
  FeedbackTransmission,
  FeedbackTransmissionInput,
  FeedbackTransmitInput,
  FeedbackUpdateInput,
  GeneratorBundleInput,
  GeneratorRepoDeposit,
  GeneratorRun,
  GeneratorRunSummary,
  GeneratorStripeInput,
  GeneratorStripeResult,
  ModuleAsset,
  ModuleAssetFromPieceInput,
  ModuleAssetInput,
  ModuleSpecDraft,
  ModuleSpecDraftInput,
  ModuleSpecDraftProposeInput,
  ModuleSpecExportPreview,
  ModuleSpecExportResult,
  PipelineApproveInput,
  PipelineDataRunInput,
  PipelineEnvironment,
  PipelineEnvironmentInput,
  PipelineEnvironmentsPayload,
  PipelineExecuteInput,
  PipelineGateTests,
  PipelineJourney,
  PipelineKind,
  PipelineLicenseResult,
  PipelineRun,
  PipelineRunSummary,
  PipelineTenantOption,
  PreviewAcknowledgeInput,
  PreviewDataset,
  PreviewDatasetCreateInput,
  PreviewDatasetSummary,
  PreviewDatasetUpdateInput,
  PreviewGenerateInput,
  PreviewRefreshInput,
  PreviewRefreshResult,
  PreviewShareCreated,
  PreviewShareInput,
  SpecDocument,
  TestPlan,
  TestPlanSummary,
  TestResult,
  TestResultInput,
  TestRun,
  TestRunSummary,
} from './contracts';
import {
  NotServedError,
  type ModuleStudioRepository,
  type ModuleStudioResource,
  type ScreenCodeSource,
  type ScreenCodeSourceCheck,
} from './ModuleStudioRepository';
import { sendExactJson } from './prototypeHttp';
import type { PreparedHtml, PreparedImage, PrototypeUploadFiles } from '../prototypes/files';
import type {
  AiRoute,
  PrototypeAcknowledgeInput,
  PrototypeAnalysis,
  PrototypeAnalysisApplyInput,
  PrototypeAnalysisApplyResult,
  PrototypeAnalysisEstimate,
  PrototypeAnalysisInput,
  PrototypeAnalysisRejectInput,
  PrototypeAnalysisSummary,
  PrototypeImageInput,
  PrototypeImport,
  PrototypeImportInput,
  PrototypeImportPatch,
  PrototypeImportSummary,
  PrototypeInventory,
  PrototypePresentationResponse,
  PrototypePrompt,
  PrototypeSourceInput,
  PrototypeSnapshotInput,
  PrototypeSnapshotResult,
} from './contracts';
import type {
  ModuleSpecDecisionInput,
  ModuleSpecDiffSection,
  ModuleSpecDocument,
  ModuleSpecProposeInput,
  ModuleSpecRepairPreview,
  ModuleSpecVersion,
  ModuleSpecVersionSummary,
  ValidationReport,
} from './moduleSpecContracts';
import { apiAzureUrl } from '@/uptk/core/api/contants';
import axios from 'axios';

const BASE = '/api/v1/module-studio';
/** UPT-2095 §11.2 : versions, décision et validation de Module Spec. */
export const BASE_SPEC = '/api/v1/module-spec';

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Identifiant attribué par le serveur (uuid). Un plan importé d'une page de recette ou dupliqué porte encore un
 * identifiant local (« tp-… ») : il se crée par POST, le serveur attribue alors ses uuid au plan, aux scènes et aux étapes.
 */
export const isServerId = (id: string | null | undefined): id is string =>
  typeof id === 'string' && UUID_PATTERN.test(id);

const fail = (resource: ModuleStudioResource, method: string): never => {
  throw new NotServedError(resource, method);
};

/**
 * Délai axios du POST …/analyses (analyse asynchrone, UPT-2096) : le serveur répond 202 dès la ligne `running`
 * enregistrée, sans attendre le modèle ; la fin se suit par GET (analysisPolling). Délai normal, à la place de l'attente
 * de l'ancien appel synchrone (timeoutSeconds + 15 s).
 */
export const ANALYSIS_REQUEST_TIMEOUT_MS = 30_000;

/** UPT-2079 L3 : le catalogue (`GET /catalog`) est gardé en mémoire de module dix minutes (même durée que le cache serveur). */
export const CATALOG_CACHE_MS = 10 * 60 * 1000;
let catalogCache: { at: number; value: DesignerCatalog } | null = null;

/** Tests : oublie le catalogue mémorisé. */
export const resetDesignerCatalogCache = (): void => {
  catalogCache = null;
};

const strings = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === 'string') : [];

/** Forme normalisée du catalogue (tableaux absents → `[]`). */
const normalizeCatalog = (raw: unknown): DesignerCatalog => {
  const source = (raw ?? {}) as Partial<DesignerCatalog>;
  return {
    schemaVersion: typeof source.schemaVersion === 'string' ? source.schemaVersion : '1.1',
    fieldTypes: strings(source.fieldTypes),
    validators: strings(source.validators),
    transformers: strings(source.transformers),
    classificationLevels: strings(source.classificationLevels),
    coreCapabilities: strings(source.coreCapabilities),
    locales: strings(source.locales),
    modules: Array.isArray(source.modules)
      ? source.modules
          .filter((entry) => entry && typeof entry === 'object' && typeof entry.code === 'string')
          .map((entry) => ({
            code: entry.code,
            version: typeof entry.version === 'string' ? entry.version : '',
            ownerOrganisation: typeof entry.ownerOrganisation === 'string' ? entry.ownerOrganisation : '',
            own: entry.own === true,
            entities: strings(entry.entities),
          }))
      : [],
    conventions: typeof source.conventions === 'string' ? source.conventions : '',
  };
};

/** `/api/v1/module-studio/dossiers/{code}/preview-datasets` (UPT-2079 L4). */
const previewBase = (dossierId: string): string =>
  `${BASE}/dossiers/${encodeURIComponent(dossierId)}/preview-datasets`;
const previewPath = (dossierId: string, datasetId: string): string =>
  `${previewBase(dossierId)}/${encodeURIComponent(datasetId)}`;

/** `/api/v1/module-studio/dossiers/{code}/generation-runs` (UPT-2079 L5). */
const generatorBase = (dossierId: string): string =>
  `${BASE}/dossiers/${encodeURIComponent(dossierId)}/generation-runs`;
const generatorPath = (dossierId: string, runId: string): string =>
  `${generatorBase(dossierId)}/${encodeURIComponent(runId)}`;

/** `/api/v1/module-studio/pipeline/…` (UPT-2079 L6a) ; `encodeURIComponent` sur chaque identifiant. */
const pipelineEnvironmentPath = (code: string): string => `${BASE}/pipeline/environments/${encodeURIComponent(code)}`;
const pipelineRunPath = (id: string): string => `${BASE}/pipeline/runs/${encodeURIComponent(id)}`;
/** UPT-2079 L6c : `/api/v1/module-studio/pipeline/modules/{code}/…` (conditions, parcours, ouverture d'un environnement). */
const pipelineModulePath = (moduleCode: string): string => `${BASE}/pipeline/modules/${encodeURIComponent(moduleCode)}`;

/** UPT-2079 L10a (web §4.2) : retours de recette classés, `encodeURIComponent` sur chaque identifiant ; les gestes sont des routes FIXES (jamais `POST /feedback/{id}`). */
const FEEDBACK_BASE = `${BASE}/feedback`;
const feedbackPath = (id: string): string => `${FEEDBACK_BASE}/${encodeURIComponent(id)}`;

/** Corps d'un `POST` ou d'un `PUT` du contrat : les champs absents (`undefined`) ne sont pas envoyés ; `null` est une valeur et part tel quel. */
const withoutUndefined = <T extends object>(input: T): T =>
  Object.fromEntries(Object.entries(input).filter(([, value]) => value !== undefined)) as T;

/** Locataires lus hors de `BASE` : route existante du socle (droit `USER` LIST), jamais une route de Module Studio. */
const TENANT_OPTIONS_PATH = '/api/tenants/options';

/**
 * `GET /api/tenants/options` : liste nue ou enveloppée dans `data` ; seuls l'identifiant et le nom sont retenus
 * (`name`, sinon `displayname`, sinon l'identifiant), triés par nom. Une entrée sans identifiant est ignorée.
 */
export const parsePipelineTenantOptions = (raw: unknown): PipelineTenantOption[] => {
  const wrapped = raw as { data?: unknown } | null | undefined;
  const list = Array.isArray(raw) ? raw : Array.isArray(wrapped?.data) ? (wrapped?.data as unknown[]) : [];
  const options: PipelineTenantOption[] = [];
  for (const entry of list) {
    if (typeof entry !== 'object' || entry === null) continue;
    const row = entry as Record<string, unknown>;
    const tenantId = typeof row.tenantId === 'string' ? row.tenantId.trim() : '';
    if (tenantId === '') continue;
    const label = [row.name, row.displayname].find((value) => typeof value === 'string' && value.trim() !== '');
    options.push({ tenantId, name: typeof label === 'string' ? label.trim() : tenantId });
  }
  return options.sort((left, right) => left.name.localeCompare(right.name));
};

const blobToText = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
    reader.onerror = () => reject(new Error('read failed'));
    reader.readAsText(blob, 'utf-8');
  });

/**
 * Une lecture en `text` ou en `blob` reçoit aussi ses ERREURS sous cette forme : le corps `{code, message}` du serveur arrive en
 * chaîne ou en `Blob`. On le relit en objet pour que `readDesignError` retrouve le code (404 `generator.file.not_found`,
 * 409 `generator.bundle.not_available`) ; un corps qui n'est pas du JSON reste tel quel (l'erreur garde son statut).
 */
const withParsedErrorBody = async (error: unknown): Promise<unknown> => {
  const response = (error as { response?: { data?: unknown } } | null)?.response;
  if (!response) return error;
  let text: string | null = null;
  if (typeof response.data === 'string') {
    text = response.data;
  } else if (typeof Blob !== 'undefined' && response.data instanceof Blob) {
    text = await blobToText(response.data).catch(() => null);
  }
  if (text !== null) {
    try {
      const parsed: unknown = JSON.parse(text);
      if (parsed && typeof parsed === 'object') response.data = parsed;
    } catch {
      // corps non JSON : l'erreur garde son statut, sans code
    }
  }
  return error;
};

/**
 * `BaseApi.get(path, payload)` prend les PARAMÈTRES DE REQUÊTE (pas une configuration axios) et ne permet ni `responseType: 'text'`
 * (un fichier JSON serait relu en objet) ni `blob` : ces deux lectures passent par `axios.request` avec les en-têtes de
 * `BaseApi.getOptions` (jeton et contexte SaaS), comme `validateModuleSpecText`.
 */
const getGeneratorContent = async <T>(
  path: string,
  responseType: 'text' | 'blob',
  params?: Record<string, string>
): Promise<T> => {
  const options = await BaseApi.getOptions(undefined);
  try {
    const response = await axios.request<T>({
      method: 'GET',
      url: apiAzureUrl() + path,
      headers: options.headers,
      responseType,
      ...(params ? { params } : {}),
      ...(responseType === 'text' ? { transformResponse: [(data: unknown) => data] } : {}),
    });
    return response.data;
  } catch (error) {
    throw await withParsedErrorBody(error);
  }
};

/** Octets d'une ressource (UPT-2079 L14a) : `responseType: 'arraybuffer'`, en-têtes de `BaseApi.getOptions` (jeton et contexte SaaS). */
const getArrayBuffer = async (path: string): Promise<ArrayBuffer> => {
  const options = await BaseApi.getOptions(undefined);
  try {
    const response = await axios.request<ArrayBuffer>({
      method: 'GET',
      url: apiAzureUrl() + path,
      headers: options.headers,
      responseType: 'arraybuffer',
    });
    return response.data;
  } catch (error) {
    throw await withParsedErrorBody(error);
  }
};

/** Vrai quand l'erreur est un 404 portant ce code (`{code, message}` du serveur). */
const isNotFound = (error: unknown, code: string): boolean => {
  const response = (error as { response?: { status?: number; data?: { code?: string } } })?.response;
  return response?.status === 404 && response.data?.code === code;
};

export class RestRepository implements ModuleStudioRepository {
  getFaithfulState: ModuleStudioRepository['getFaithfulState'] = scope => restFaithfulRepository.get(scope);
  putFaithfulState: ModuleStudioRepository['putFaithfulState'] = (scope, input) => restFaithfulRepository.put(scope, input);
  async getTranslationPreference(dossierId: string): Promise<TranslationPreference> {
    const response = await BaseApi.get(`${BASE_SPEC}/dossiers/${encodeURIComponent(dossierId)}/translation-preference`);
    return response.data as TranslationPreference;
  }
  async setTranslationPreference(dossierId: string, preference: TranslationPreference): Promise<TranslationPreference> {
    const response = await BaseApi.put(`${BASE_SPEC}/dossiers/${encodeURIComponent(dossierId)}/translation-preference`, preference);
    return response.data as TranslationPreference;
  }
  async createStarterTestPlan(dossierId: string, version: number): Promise<StarterTestPlanResult> {
    const response = await BaseApi.post(`${BASE_SPEC}/dossiers/${encodeURIComponent(dossierId)}/versions/${version}/starter-test-plan`, {});
    return response.data as StarterTestPlanResult;
  }
  async estimatePrototypeConversion(importId: string, input: PrototypeConversionInput): Promise<PrototypeConversionEstimate> {
    return sendExactJson('POST', `${BASE}/prototype-imports/${encodeURIComponent(importId)}/conversion-jobs/estimate`, input);
  }
  async startPrototypeConversion(importId: string, input: PrototypeConversionInput): Promise<PrototypeConversionJob> {
    return sendExactJson('POST', `${BASE}/prototype-imports/${encodeURIComponent(importId)}/conversion-jobs`, input);
  }
  async listPrototypeConversions(importId: string): Promise<PrototypeConversionJob[]> {
    return (await BaseApi.get(`${BASE}/prototype-imports/${encodeURIComponent(importId)}/conversion-jobs`)).data;
  }
  async getPrototypeConversion(importId: string, id: string): Promise<PrototypeConversionJob> {
    return (await BaseApi.get(`${BASE}/prototype-imports/${encodeURIComponent(importId)}/conversion-jobs/${encodeURIComponent(id)}`)).data;
  }
  async cancelPrototypeConversion(importId: string, id: string): Promise<PrototypeConversionJob> {
    return (await BaseApi.post(`${BASE}/prototype-imports/${encodeURIComponent(importId)}/conversion-jobs/${encodeURIComponent(id)}/cancel`, {})).data;
  }
  async resumePrototypeConversion(importId: string, id: string): Promise<PrototypeConversionJob> {
    return (await BaseApi.post(`${BASE}/prototype-imports/${encodeURIComponent(importId)}/conversion-jobs/${encodeURIComponent(id)}/resume`, {})).data;
  }
  async estimateScreenAnalysis(importId: string, input: ScreenAnalysisInput): Promise<import('./screenAnalysisContracts').ScreenAnalysisEstimate> {
    return sendExactJson('POST', BASE+'/prototype-imports/'+encodeURIComponent(importId)+'/screen-analyses/estimate', input);
  }
  async startScreenAnalysis(importId: string, input: ScreenAnalysisInput): Promise<ScreenAnalysis> {
    return sendExactJson<ScreenAnalysis>('POST', BASE+'/prototype-imports/'+encodeURIComponent(importId)+'/screen-analyses', input);
  }
  async listScreenAnalyses(importId: string): Promise<ScreenAnalysis[]> {
    return (await BaseApi.get(BASE+'/prototype-imports/'+encodeURIComponent(importId)+'/screen-analyses')).data;
  }
  async getScreenAnalysis(importId: string, id: string): Promise<ScreenAnalysis> {
    return (await BaseApi.get(BASE+'/prototype-imports/'+encodeURIComponent(importId)+'/screen-analyses/'+encodeURIComponent(id))).data;
  }
  async applyScreenAnalysis(importId: string, id: string): Promise<ScreenAnalysis> {
    return (await BaseApi.post(BASE+'/prototype-imports/'+encodeURIComponent(importId)+'/screen-analyses/'+encodeURIComponent(id)+'/apply',{})).data;
  }
  async rejectScreenAnalysis(importId: string, id: string): Promise<ScreenAnalysis> {
    return (await BaseApi.post(BASE+'/prototype-imports/'+encodeURIComponent(importId)+'/screen-analyses/'+encodeURIComponent(id)+'/reject',{})).data;
  }
  async produceScreenCode(importId:string,id:string,input:{blockKey:string;includeMaskedMarkup:boolean}):Promise<ScreenAnalysis>{
    return (await BaseApi.post(BASE+'/prototype-imports/'+encodeURIComponent(importId)+'/screen-analyses/'+encodeURIComponent(id)+'/code',input)).data;
  }
  async zoneCode(importId:string,input:ZoneCodeInput,estimate:boolean):Promise<ZoneCodeResult>{
    const path=estimate?'/zone-code/estimate':'/zone-code';
    // 23.10.2026 : lecteur strict côté serveur (aucun champ inconnu) → corps exact, sans les clés de contexte injectées par BaseApi.
    return sendExactJson<ZoneCodeResult>('POST', BASE+'/prototype-imports/'+encodeURIComponent(importId)+'/screen-analyses'+path, input);
  }
  async getScreenCodeSource(dossierId:string,artifactRef:string,path:string):Promise<ScreenCodeSource>{
    // 02.11.2026 (GAP-76, recette) : BaseApi.get n'accepte pas de config axios, `{params}` partait dans l'URL (400) : la requête est explicite.
    return (await BaseApi.get(BASE+'/dossiers/'+encodeURIComponent(dossierId)+'/code-sources/'+encodeURIComponent(artifactRef)+'?path='+encodeURIComponent(path))).data;
  }
  async checkScreenCodeSource(dossierId:string,artifactRef:string,path:string,source:string):Promise<ScreenCodeSourceCheck>{
    return (await BaseApi.post(BASE+'/dossiers/'+encodeURIComponent(dossierId)+'/code-sources/'+encodeURIComponent(artifactRef)+'/check?path='+encodeURIComponent(path),{source})).data;
  }
  async saveScreenCodeSource(dossierId:string,artifactRef:string,path:string,source:string):Promise<ScreenCodeSource>{
    return (await BaseApi.put(BASE+'/dossiers/'+encodeURIComponent(dossierId)+'/code-sources/'+encodeURIComponent(artifactRef)+'?path='+encodeURIComponent(path),{source})).data;
  }
  async listSimulations(dossierId: string): Promise<SimulationSummary[]> {
    const response = await BaseApi.get(`${BASE}/dossiers/${encodeURIComponent(dossierId)}/simulations`);
    return response.data as SimulationSummary[];
  }
  async getSimulation(dossierId: string, simulationId: string): Promise<Simulation | null> {
    try {
    const response = await BaseApi.get(`${BASE}/dossiers/${encodeURIComponent(dossierId)}/simulations/${encodeURIComponent(simulationId)}`);
    return response.data as Simulation | null;
    } catch (error) {
      const response = (error as {response?:{status?:number;data?:{code?:string;issues?:GeneratorIssue[]}}}).response;
      if (response?.status===404 && response.data?.code==='simulation.not_found') return null;
      throw error;
    }
  }
  async createSimulation(dossierId: string, input: SimulationCreateInput): Promise<Simulation> {
    try {
    const response = { data: await executeStudioCommand<Simulation>(dossierId, 'simulate', { input }) };
    return response.data as Simulation;
    } catch (error) {
      const response = (error as {response?:{status?:number;data?:{code?:string;issues?:GeneratorIssue[]}}}).response;
      if (response?.status===409 && response.data?.code==='simulation.spec_errors') throw new SimulationSpecErrorsError(response.data.issues ?? []);
      throw error;
    }
  }
  async updateSimulation(dossierId: string, simulationId: string, input: SimulationUpdateInput): Promise<Simulation> {
    const response = await BaseApi.put(`${BASE}/dossiers/${encodeURIComponent(dossierId)}/simulations/${encodeURIComponent(simulationId)}`, input);
    return response.data as Simulation;
  }
  async generateSimulationData(dossierId: string, simulationId: string, input: SimulationGenerateInput): Promise<Simulation> {
    const response = await BaseApi.post(`${BASE}/dossiers/${encodeURIComponent(dossierId)}/simulations/${encodeURIComponent(simulationId)}/generate`, input);
    return response.data as Simulation;
  }
  async refreshSimulation(dossierId: string, simulationId: string, input: SimulationRefreshInput): Promise<SimulationRefreshResult> {
    try {
    const response = await BaseApi.post(`${BASE}/dossiers/${encodeURIComponent(dossierId)}/simulations/${encodeURIComponent(simulationId)}/refresh`, input);
    return response.data as SimulationRefreshResult;
    } catch (error) {
      const response = (error as {response?:{status?:number;data?:{code?:string;issues?:GeneratorIssue[]}}}).response;
      if (response?.status===409 && response.data?.code==='simulation.spec_errors') throw new SimulationSpecErrorsError(response.data.issues ?? []);
      throw error;
    }
  }
  async acknowledgeSimulationWarnings(dossierId: string, simulationId: string, input: SimulationAcknowledgeInput): Promise<Simulation> {
    const response = await BaseApi.post(`${BASE}/dossiers/${encodeURIComponent(dossierId)}/simulations/${encodeURIComponent(simulationId)}/acknowledge`, input);
    return response.data as Simulation;
  }
  async deleteSimulation(dossierId: string, simulationId: string): Promise<void> {
    await BaseApi.delete(`${BASE}/dossiers/${encodeURIComponent(dossierId)}/simulations/${encodeURIComponent(simulationId)}`);
  }
  async createSimulationShare(dossierId: string, simulationId: string, input: SimulationShareInput): Promise<SimulationShareCreated> {
    const response = await BaseApi.post(`${BASE}/dossiers/${encodeURIComponent(dossierId)}/simulations/${encodeURIComponent(simulationId)}/share`, input);
    return response.data as SimulationShareCreated;
  }
  async revokeSimulationShare(dossierId: string, simulationId: string): Promise<void> {
    await BaseApi.delete(`${BASE}/dossiers/${encodeURIComponent(dossierId)}/simulations/${encodeURIComponent(simulationId)}/share`);
  }

  async listIdeas(): Promise<Idea[]> { const response = await BaseApi.get(`${BASE}/ideas?limit=20`); return response.data as Idea[]; }
  async createIdea(input: IdeaInput): Promise<Idea> { const response = await BaseApi.post(`${BASE}/ideas`, input); return response.data as Idea; }
  async getIdea(id: string): Promise<Idea> { const response = await BaseApi.get(`${BASE}/ideas/${encodeURIComponent(id)}`); return response.data as Idea; }
  async updateIdea(id: string, input: IdeaInput): Promise<Idea> { const response = await BaseApi.put(`${BASE}/ideas/${encodeURIComponent(id)}`, input); return response.data as Idea; }
  async ideaEstimate(id: string, phase: 'brief' | 'prototype'): Promise<IdeaEstimate> { const response = await BaseApi.get(`${BASE}/ideas/${encodeURIComponent(id)}/estimate`, { params: { phase } }); return response.data as IdeaEstimate; }
  async fetchIdeaRepository(id: string): Promise<Idea> { const response = await BaseApi.post(`${BASE}/ideas/${encodeURIComponent(id)}/fetch`, {}); return response.data as Idea; }
  async getIdeaRepository(id: string): Promise<IdeaRepositoryTree> { const response = await BaseApi.get(`${BASE}/ideas/${encodeURIComponent(id)}/repository?format=tree`); return response.data as IdeaRepositoryTree; }
  async startBrief(id: string): Promise<Idea> { const response = await BaseApi.post(`${BASE}/ideas/${encodeURIComponent(id)}/brief`, {}); return response.data as Idea; }
  async acceptBrief(id: string, input: { text: string }): Promise<Idea> { const response = await BaseApi.put(`${BASE}/ideas/${encodeURIComponent(id)}/brief`, input); return response.data as Idea; }
  async startPrototype(id: string, input?: { remarksText?: string }): Promise<Idea> { const response = await BaseApi.post(`${BASE}/ideas/${encodeURIComponent(id)}/prototype`, input ?? {}); return response.data as Idea; }
  async getPrototypeHtml(id: string): Promise<string> { const response = await BaseApi.get(`${BASE}/ideas/${encodeURIComponent(id)}/prototype`, { responseType: 'text', headers: { Accept: 'text/plain' } }); return response.data as string; }
  async getIdeaPrompt(id: string): Promise<string> { const response = await BaseApi.get(`${BASE}/ideas/${encodeURIComponent(id)}/prompt.md`, { responseType: 'text', headers: { Accept: 'text/markdown' } }); return response.data as string; }
  async cancelIdea(id: string): Promise<Idea> { const response = await BaseApi.post(`${BASE}/ideas/${encodeURIComponent(id)}/cancel`, {}); return response.data as Idea; }
  async transferIdea(id: string, input: IdeaTransferInput): Promise<IdeaTransfer> { const response = await BaseApi.post(`${BASE}/ideas/${encodeURIComponent(id)}/transfer`, input); return response.data as IdeaTransfer; }
  async deleteIdea(id: string): Promise<void> { await BaseApi.delete(`${BASE}/ideas/${encodeURIComponent(id)}`); return; }

  async listDossiers(): Promise<DesignDossier[]> {
    const response = await BaseApi.get(`${BASE}/dossiers`);
    return response.data as DesignDossier[];
  }

  /** 404 dossier.not_found → null (la fiche affiche « introuvable », comportement L0). */
  // E2E-FIX1 (GAP-9, PROBE-LIGHT) : les lecteurs du fil (rail, fil, actions, plan) qui relisent ensemble après une mutation partagent
  // la même requête en vol ; aucune réponse n'est réutilisée après son arrivée (pas de cache, donc jamais d'état périmé).
  private readonly threadReads = new Map<string, Promise<DossierThread>>();
  getDossierThread(id: string, displayedAnalysis?: string): Promise<DossierThread> {
    const key = JSON.stringify([id, displayedAnalysis ?? null]);
    const pending = this.threadReads.get(key);
    if (pending) return pending;
    const read = (async () => {
      try {
        const response = await BaseApi.get(`${BASE}/dossiers/${encodeURIComponent(id)}/thread`, displayedAnalysis ? { displayedAnalysis } : undefined);
        return response.data as DossierThread;
      } finally { this.threadReads.delete(key); }
    })();
    this.threadReads.set(key, read);
    return read;
  }

  async getDossier(id: string): Promise<DesignDossier | null> {
    try {
      const response = await BaseApi.get(`${BASE}/dossiers/${encodeURIComponent(id)}`);
      return (response.data as DesignDossier) ?? null;
    } catch (error) {
      if (isNotFound(error, 'dossier.not_found')) {
        return null;
      }
      throw error;
    }
  }

  async saveDossier(input: DesignDossierInput): Promise<DesignDossier> {
    if (input.id) {
      const response = await BaseApi.put(
        `${BASE}/dossiers/${encodeURIComponent(input.id)}`,
        input
      );
      return response.data as DesignDossier;
    }
    const response = await BaseApi.post(`${BASE}/dossiers`, input);
    return response.data as DesignDossier;
  }

  /* ── Dossiers servis (UPT-2079 L2, web §4.2) ─────────────────────────── */

  async deleteDossier(id: string): Promise<void> {
    await BaseApi.delete(`${BASE}/dossiers/${encodeURIComponent(id)}`);
  }

  async getModuleSpec(dossierId: string): Promise<DesignModuleSpecPayload> {
    const response = await BaseApi.get(`${BASE}/dossiers/${encodeURIComponent(dossierId)}/module-spec`);
    return response.data as DesignModuleSpecPayload;
  }

  async putModuleSpec(dossierId: string, input: DesignModuleSpecInput): Promise<DesignDossier> {
    const response = await BaseApi.put(
      `${BASE}/dossiers/${encodeURIComponent(dossierId)}/module-spec`,
      input
    );
    return response.data as DesignDossier;
  }

  /* ── Specs (UPT-2079 L2, web §4.2) ───────────────────────────────────── */

  async listSpecs(dossierId: string): Promise<SpecDocument[]> {
    const response = await BaseApi.get(
      `${BASE}/dossiers/${encodeURIComponent(dossierId)}/specs`
    );
    return response.data as SpecDocument[];
  }

  /** 404 spec.not_found → null (modèle `getPrototypePrompt`). */
  async getSpec(id: string): Promise<DesignSpecDetail | null> {
    try {
      const response = await BaseApi.get(`${BASE}/specs/${encodeURIComponent(id)}`);
      return response.data as DesignSpecDetail;
    } catch (error) {
      if (isNotFound(error, 'spec.not_found')) {
        return null;
      }
      throw error;
    }
  }

  async createSpec(dossierId: string, input: DesignSpecDocumentInput): Promise<DesignSpecDetail> {
    const response = await BaseApi.post(
      `${BASE}/dossiers/${encodeURIComponent(dossierId)}/specs`,
      input
    );
    return response.data as DesignSpecDetail;
  }

  async updateSpec(id: string, input: DesignSpecDocumentInput): Promise<DesignSpecDetail> {
    const response = await BaseApi.put(`${BASE}/specs/${encodeURIComponent(id)}`, input);
    return response.data as DesignSpecDetail;
  }

  async listSpecVersions(id: string): Promise<DesignSpecVersionSummary[]> {
    const response = await BaseApi.get(`${BASE}/specs/${encodeURIComponent(id)}/versions`);
    return response.data as DesignSpecVersionSummary[];
  }

  async getSpecVersion(id: string, version: number): Promise<DesignSpecVersion> {
    const response = await BaseApi.get(
      `${BASE}/specs/${encodeURIComponent(id)}/versions/${encodeURIComponent(String(version))}`
    );
    return response.data as DesignSpecVersion;
  }

  /** `params` ne contient que les bornes non nulles, chacune en chaîne décimale. */
  async diffSpec(id: string, from: number | null, to: number | null): Promise<DesignSpecDiff> {
    const params: Record<string, string> = {};
    if (from !== null) {
      params.from = String(from);
    }
    if (to !== null) {
      params.to = String(to);
    }
    const response = await BaseApi.get(`${BASE}/specs/${encodeURIComponent(id)}/diff`, params);
    return response.data as DesignSpecDiff;
  }

  async generateSpec(id: string, input: DesignSpecGenerateInput): Promise<DesignSpecDetail> {
    const response = await BaseApi.post(`${BASE}/specs/${encodeURIComponent(id)}/generate`, input);
    return response.data as DesignSpecDetail;
  }

  async syncSpec(id: string, input: DesignSpecSyncInput): Promise<DesignSpecSyncResult> {
    const response = await BaseApi.post(`${BASE}/specs/${encodeURIComponent(id)}/sync`, input);
    return response.data as DesignSpecSyncResult;
  }

  async listTestPlans(filter?: { dossierId?: string; moduleCode?: string }): Promise<TestPlanSummary[]> {
    const response = await BaseApi.get(`${BASE}/test-plans`, {
      dossierId: filter?.dossierId ?? '',
      moduleCode: filter?.moduleCode ?? '',
    });
    return response.data as TestPlanSummary[];
  }

  async getTestPlan(id: string): Promise<TestPlan | null> {
    const response = await BaseApi.get(`${BASE}/test-plans/${encodeURIComponent(id)}`);
    return (response.data as TestPlan) ?? null;
  }

  async saveTestPlan(plan: TestPlan): Promise<TestPlan> {
    const response = isServerId(plan.id)
      ? await BaseApi.put(`${BASE}/test-plans/${encodeURIComponent(plan.id)}`, plan)
      : await BaseApi.post(`${BASE}/test-plans`, plan);
    return response.data as TestPlan;
  }

  async listTestRuns(planId: string): Promise<TestRunSummary[]> {
    const response = await BaseApi.get(
      `${BASE}/test-plans/${encodeURIComponent(planId)}/runs`
    );
    return response.data as TestRunSummary[];
  }

  async getTestRun(id: string): Promise<TestRun | null> {
    const response = await BaseApi.get(`${BASE}/test-runs/${encodeURIComponent(id)}`);
    return (response.data as TestRun) ?? null;
  }

  async createTestRun(input: {
    planId: string;
    environmentCode: EnvironmentCode;
    testerName: string;
  }): Promise<TestRun> {
    const response = await BaseApi.post(`${BASE}/test-runs`, input);
    return response.data as TestRun;
  }

  async putTestResult(runId: string, stepId: string, input: TestResultInput): Promise<TestResult> {
    const response = await BaseApi.put(
      `${BASE}/test-runs/${encodeURIComponent(runId)}/results/${encodeURIComponent(stepId)}`,
      input
    );
    return response.data as TestResult;
  }

  async clearTestResult(runId: string, stepId: string): Promise<void> {
    await BaseApi.delete(
      `${BASE}/test-runs/${encodeURIComponent(runId)}/results/${encodeURIComponent(stepId)}`
    );
  }

  async validateProposedResult(
    runId: string,
    stepId: string,
    decision: 'accept' | 'reject'
  ): Promise<TestResult> {
    const response = await BaseApi.post(
      `${BASE}/test-runs/${encodeURIComponent(runId)}/results/${encodeURIComponent(stepId)}/validate`,
      { decision }
    );
    return response.data as TestResult;
  }

  async putSceneNote(runId: string, sceneId: string, noteMd: string): Promise<void> {
    await BaseApi.put(
      `${BASE}/test-runs/${encodeURIComponent(runId)}/notes/${encodeURIComponent(sceneId)}`,
      { noteMd }
    );
  }

  async closeTestRun(
    runId: string,
    input: { reportMd: string; paperclipRef: string | null }
  ): Promise<TestRun> {
    const response = await BaseApi.post(
      `${BASE}/test-runs/${encodeURIComponent(runId)}/close`,
      input
    );
    return response.data as TestRun;
  }

  /* ── Import de prototypes (UPT-2096 P1-a) ────────────────────────────── */

  async listPrototypeImports(dossierRef: string): Promise<PrototypeImportSummary[]> {
    const response = await BaseApi.get(
      `${BASE}/dossiers/${encodeURIComponent(dossierRef)}/prototype-imports`
    );
    return response.data as PrototypeImportSummary[];
  }

  async getPrototypeImport(id: string): Promise<PrototypeImport> {
    const response = await BaseApi.get(`${BASE}/prototype-imports/${encodeURIComponent(id)}`);
    return response.data as PrototypeImport;
  }

  async createPrototypeImport(
    dossierRef: string,
    input: PrototypeImportInput,
    files: PrototypeUploadFiles
  ): Promise<PrototypeImport> {
    const fd = new FormData();
    fd.append('metadata', new Blob([JSON.stringify(input)], { type: 'application/json' }));
    if (files.html) {
      fd.append('html', new Blob([files.html.text], { type: 'text/html;charset=utf-8' }), files.html.fileName);
    }
    // L2 : une partie `markdown` par document, dans l'ordre ; seul l'HTML de rang 0 part ici.
    for (const markdown of files.markdowns ?? []) {
      fd.append('markdown', new Blob([markdown.bytes as unknown as BlobPart], { type: 'text/markdown' }), markdown.fileName);
    }
    if (files.image) {
      fd.append('image', files.image.png, files.image.fileName);
    }
    const response = await BaseApi.postMultipart(
        `${BASE}/dossiers/${encodeURIComponent(dossierRef)}/commands/import?confirmed=true&requestId=${encodeURIComponent(files.commandRequestId ?? studioRequestId())}`,
      fd
    );
    return response.data as PrototypeImport;
  }

  /** L2 : source HTML de rang ≥ 1 (partie `html`, `metadata` facultative). */
  async addPrototypeSource(
    id: string,
    html: PreparedHtml,
    input: PrototypeSourceInput | null
  ): Promise<PrototypeImport> {
    const fd = new FormData();
    fd.append('html', new Blob([html.text], { type: 'text/html;charset=utf-8' }), html.fileName);
    if (input !== null) {
      fd.append('metadata', new Blob([JSON.stringify(input)], { type: 'application/json' }));
    }
    const response = await BaseApi.postMultipart(
      `${BASE}/prototype-imports/${encodeURIComponent(id)}/sources`,
      fd
    );
    return response.data as PrototypeImport;
  }

  async addPrototypeImage(
    id: string,
    image: PreparedImage,
    input: PrototypeImageInput | null
  ): Promise<PrototypeImport> {
    const fd = new FormData();
    fd.append('image', image.png, image.fileName);
    if (input !== null) {
      fd.append('metadata', new Blob([JSON.stringify(input)], { type: 'application/json' }));
    }
    const response = await BaseApi.postMultipart(
      `${BASE}/prototype-imports/${encodeURIComponent(id)}/images`,
      fd
    );
    return response.data as PrototypeImport;
  }

  async getPrototypePrompt(id: string): Promise<PrototypePrompt | null> {
    try {
      const response = await BaseApi.get(`${BASE}/prototype-imports/${encodeURIComponent(id)}/prompt`);
      return response.data as PrototypePrompt;
    } catch (error) {
      const status = (error as { response?: { status?: number; data?: { code?: string } } })?.response;
      if (status?.status === 404 && status.data?.code === 'prototype.prompt_absent') {
        return null;
      }
      throw error;
    }
  }

  /** Contenu brut d'une pièce html ou snapshot, lu en texte UTF-8 (jamais re-typé). */
  async getPrototypePieceText(id: string, pieceId: string): Promise<string> {
    const response = await BaseApi.getBlob(
      `${BASE}/prototype-imports/${encodeURIComponent(id)}/pieces/${encodeURIComponent(pieceId)}/content`
    );
    const blob = response.data as Blob;
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result ?? ''));
      reader.onerror = () => reject(new Error('read failed'));
      reader.readAsText(blob, 'utf-8');
    });
  }

  /** Contenu brut d'une pièce image : signature PNG exigée, puis re-typage image/png. */
  async getPrototypePieceImage(id: string, pieceId: string): Promise<Blob> {
    const response = await BaseApi.getBlob(
      `${BASE}/prototype-imports/${encodeURIComponent(id)}/pieces/${encodeURIComponent(pieceId)}/content`
    );
    const blob = response.data as Blob;
    const signature = await new Promise<ArrayBuffer>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as ArrayBuffer);
      reader.onerror = () => reject(new Error('read failed'));
      reader.readAsArrayBuffer(blob.slice(0, 8));
    });
    const bytes = new Uint8Array(signature);
    const expected = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
    if (bytes.length < 8 || expected.some((value, index) => bytes[index] !== value)) {
      throw Object.assign(new Error('image sans signature PNG'), { code: 'unknown' });
    }
    return new Blob([blob], { type: 'image/png' });
  }

  async deletePrototypeSnapshot(id: string, pieceId: string): Promise<PrototypeImport> {
    const response = await BaseApi.delete(BASE + "/prototype-imports/" + encodeURIComponent(id) + "/pieces/" + encodeURIComponent(pieceId));
    return response.data as PrototypeImport;
  }
  async addPrototypeSnapshot(id: string, input: PrototypeSnapshotInput): Promise<PrototypeSnapshotResult> {
    // `sourcePieceRef` seulement s'il est défini : aucun champ `undefined` dans le corps.
    const body: PrototypeSnapshotInput =
      input.sourcePieceRef === undefined
        ? { label: input.label, html: input.html }
        : { label: input.label, html: input.html, sourcePieceRef: input.sourcePieceRef };
    return sendExactJson<PrototypeSnapshotResult>(
      'POST',
      `${BASE}/prototype-imports/${encodeURIComponent(id)}/snapshots`,
      body
    );
  }

  async putPrototypeInventory(id: string, inventory: PrototypeInventory): Promise<PrototypeImport> {
    return sendExactJson<PrototypeImport>(
      'PUT',
      `${BASE}/prototype-imports/${encodeURIComponent(id)}/inventory`,
      inventory
    );
  }

  async getModuleComponents(dossierId: string): Promise<ModuleComponents> { return (await BaseApi.get(BASE + '/dossiers/' + encodeURIComponent(dossierId) + '/components')).data; }
  async refreshModuleComponents(dossierId: string): Promise<ModuleComponents> { return (await BaseApi.post(BASE + '/dossiers/' + encodeURIComponent(dossierId) + '/components/refresh', {})).data; }
  async proposeBindings(spec: ModuleSpecDocument, blocks: BindingBlock[]): Promise<BindingCandidate[][]> { return (await BaseApi.post(BASE_SPEC + '/binding-proposals', {spec, blocks})).data as BindingCandidate[][]; }
  async getCapabilityCatalog(dossierId?: string): Promise<CapabilityCatalog> { return (await BaseApi.get(BASE + '/capability-catalog' + (dossierId ? '?dossier=' + encodeURIComponent(dossierId) : ''))).data as CapabilityCatalog; }
  async getPrototypeCoverage(id: string): Promise<CoverageReport> { return (await BaseApi.get(BASE + '/prototype-imports/' + encodeURIComponent(id) + '/coverage')).data as CoverageReport; }
  async getGapReport(id: string, version: number): Promise<GapReport> { return (await BaseApi.get(BASE_SPEC + '/dossiers/' + encodeURIComponent(id) + '/versions/' + encodeURIComponent(String(version)) + '/gap-report')).data as GapReport; }
  async getSpecCoverage(dossierId: string, version: number): Promise<CoverageReport> { return (await BaseApi.get(BASE_SPEC + '/dossiers/' + encodeURIComponent(dossierId) + '/versions/' + encodeURIComponent(String(version)) + '/coverage')).data as CoverageReport; }
  async getNavigationProposal(dossierId: string, version: number, importId?: string, options?: { draft?: boolean }): Promise<NavigationProposal> {
    const url = BASE_SPEC + '/dossiers/' + encodeURIComponent(dossierId) + '/versions/' + encodeURIComponent(String(version)) + '/navigation-proposal';
    const query = [importId ? 'importId=' + encodeURIComponent(importId) : '', options?.draft ? 'draft=true' : ''].filter(Boolean).join('&');
    return (await BaseApi.get(query ? url + '?' + query : url)).data as NavigationProposal;
  }
  async getPrototypeInventory(id: string): Promise<PrototypeInventory> {
    const response = await BaseApi.get(`${BASE}/prototype-imports/${encodeURIComponent(id)}/inventory`);
    return response.data as PrototypeInventory;
  }

  async patchPrototypeImport(id: string, patch: PrototypeImportPatch): Promise<PrototypeImport> {
    return sendExactJson<PrototypeImport>(
      'PATCH',
      `${BASE}/prototype-imports/${encodeURIComponent(id)}`,
      patch
    );
  }

  async prototypeRemovalImpact(id: string) {
    return (await BaseApi.get(`${BASE}/prototype-imports/${encodeURIComponent(id)}/removal-impact`)).data as Awaited<ReturnType<ModuleStudioRepository['prototypeRemovalImpact']>>;
  }
  async restorePrototypeImport(id: string): Promise<void> {
    const source = await this.getPrototypeImport(id); await executeStudioCommand(source.dossierRef, 'restore', { importId: id });
  }
  async hidePrototypeImport(id: string): Promise<void> {
    await BaseApi.post(`${BASE}/prototype-imports/${encodeURIComponent(id)}/hide`, {});
  }
  async deletePrototypeImport(id: string): Promise<void> {
    const source = await this.getPrototypeImport(id); await executeStudioCommand(source.dossierRef, 'retire', { importId: id });
  }

  /* ── Analyse des prototypes (UPT-2096 P1-b, web §4.2) ───────────────── */

  async acknowledgePrototypeWarnings(id: string, input: PrototypeAcknowledgeInput): Promise<PrototypeImport> {
    return sendExactJson<PrototypeImport>(
      'POST',
      `${BASE}/prototype-imports/${encodeURIComponent(id)}/warnings/acknowledge`,
      input
    );
  }

  /** L2 : GET …/analyses/estimate (aucun appel au modèle, aucune écriture). */
  async getPrototypeAnalysisEstimate(id: string): Promise<PrototypeAnalysisEstimate> {
    const response = await BaseApi.get(
      `${BASE}/prototype-imports/${encodeURIComponent(id)}/analyses/estimate`
    );
    rememberStudioEstimate(id, response.data as PrototypeAnalysisEstimate);
    return response.data as PrototypeAnalysisEstimate;
  }

  async getAiRoute(): Promise<AiRoute> {
    const response = await BaseApi.get(`${BASE}/ai-route`);
    return response.data as AiRoute;
  }

  /** POST …/analyses : 202 (ou 201 d'un ancien serveur) avec l'analyse ; statut `running` → le panneau suit par GET. */
  async startPrototypeAnalysis(id: string, input: PrototypeAnalysisInput): Promise<PrototypeAnalysis> { const source = await this.getPrototypeImport(id); return executeEstimatedAnalysis(source.dossierRef, id, input); }

  async listPrototypeAnalyses(id: string): Promise<PrototypeAnalysisSummary[]> {
    const response = await BaseApi.get(`${BASE}/prototype-imports/${encodeURIComponent(id)}/analyses`);
    return response.data as PrototypeAnalysisSummary[];
  }

  async getPrototypeAnalysis(id: string, analysisId: string): Promise<PrototypeAnalysis> {
    const response = await BaseApi.get(
      `${BASE}/prototype-imports/${encodeURIComponent(id)}/analyses/${encodeURIComponent(analysisId)}`
    );
    return response.data as PrototypeAnalysis;
  }

  async applyPrototypeAnalysis(
    id: string,
    analysisId: string,
    input: PrototypeAnalysisApplyInput
  ): Promise<PrototypeAnalysisApplyResult> {
    return sendExactJson<PrototypeAnalysisApplyResult>(
      'POST',
      `${BASE}/prototype-imports/${encodeURIComponent(id)}/analyses/${encodeURIComponent(analysisId)}/apply`,
      input
    );
  }

  async rejectPrototypeAnalysis(
    id: string,
    analysisId: string,
    input: PrototypeAnalysisRejectInput
  ): Promise<PrototypeAnalysis> {
    return sendExactJson<PrototypeAnalysis>(
      'POST',
      `${BASE}/prototype-imports/${encodeURIComponent(id)}/analyses/${encodeURIComponent(analysisId)}/reject`,
      input
    );
  }

  /* ── Versions de Module Spec (UPT-2095 §11.2, web §11.2) ─────────────── */

  async listModuleSpecVersions(dossierId: string): Promise<ModuleSpecVersionSummary[]> {
    const response = await BaseApi.get(`${BASE_SPEC}/dossiers/${encodeURIComponent(dossierId)}/versions`);
    return response.data as ModuleSpecVersionSummary[];
  }

  async getModuleSpecVersion(dossierId: string, version: number): Promise<ModuleSpecVersion> {
    const response = await BaseApi.get(
      `${BASE_SPEC}/dossiers/${encodeURIComponent(dossierId)}/versions/${encodeURIComponent(String(version))}`
    );
    return response.data as ModuleSpecVersion;
  }

  async diffModuleSpecVersions(dossierId: string, from: number, to: number): Promise<ModuleSpecDiffSection[]> {
    const response = await BaseApi.get(
      `${BASE_SPEC}/dossiers/${encodeURIComponent(dossierId)}/versions/${encodeURIComponent(String(from))}/diff/${encodeURIComponent(String(to))}`
    );
    return response.data as ModuleSpecDiffSection[];
  }

  /** Corps `{spec, source, comment}` (les clés absentes ne sont pas envoyées) ; `source` absent = `human` côté serveur. */
  async proposeModuleSpecVersion(dossierId: string, input: ModuleSpecProposeInput): Promise<ModuleSpecVersion> {
    const body: ModuleSpecProposeInput = { spec: input.spec };
    if (input.source !== undefined) body.source = input.source;
    if (input.comment !== undefined) body.comment = input.comment;
    const response = await BaseApi.post(`${BASE_SPEC}/dossiers/${encodeURIComponent(dossierId)}/versions`, body);
    return response.data as ModuleSpecVersion;
  }

  async decideModuleSpecVersion(
    dossierId: string,
    version: number,
    input: ModuleSpecDecisionInput
  ): Promise<ModuleSpecVersion> { return executeStudioCommand(dossierId, input.decision === 'accept' ? 'accept' : 'reject', { version, input }); }

  /** Aperçu de la réparation (UPT-2079 L11 §9) : lecture seule, rien n'est écrit. */
  async previewRelations(input:RelationsInput):Promise<RelationsResult> {return sendExactJson('POST', BASE_SPEC+'/relations/preview',input);}
  async applyRelations(input:RelationsInput):Promise<RelationsResult> {return sendExactJson('POST', BASE_SPEC+'/relations/apply',input);}
  async previewModuleSpecRepair(dossierId: string, version: number): Promise<ModuleSpecRepairPreview> {
    const response = await BaseApi.get(
      `${BASE_SPEC}/dossiers/${encodeURIComponent(dossierId)}/versions/${encodeURIComponent(String(version))}/repair`
    );
    return response.data as ModuleSpecRepairPreview;
  }

  /** Crée une nouvelle version `proposed` réparée (201) ; la version `version` reste inchangée. Corps vide : tout se lit côté serveur. */
  async repairModuleSpecVersion(dossierId: string, version: number): Promise<ModuleSpecVersion> {
    return executeStudioCommand(dossierId, 'repair', { version });
  }

  /** Contrôle de livraison à blanc (UPT-2079 L12b) : lecture seule, rien n'est écrit, pas de cache. */
  async estimateModuleSpecTranslation(dossierId: string, version: number): Promise<ModuleSpecTranslationEstimate> {
    const response = await BaseApi.get(`${BASE_SPEC}/dossiers/${encodeURIComponent(dossierId)}/versions/${encodeURIComponent(String(version))}/translate`);
    return response.data as ModuleSpecTranslationEstimate;
  }
  async translateModuleSpecVersion(dossierId: string, version: number): Promise<ModuleSpecVersion> {
    // Aucun corps, même vide, et délai couvrant les six lots plus leurs éventuelles corrections.
    return sendExactJson<ModuleSpecVersion>('POST', `${BASE_SPEC}/dossiers/${encodeURIComponent(dossierId)}/versions/${encodeURIComponent(String(version))}/translate`, undefined, 600_000);
  }

  /**
   * UPT-2079 P2b : traduction en tâche de fond. Aucun corps (règle de L12c conservée : le navigateur n'envoie aucun texte) et le délai par défaut, la création rend 202 sans
   * attendre le fournisseur ; le délai de 600 000 ms ne concerne que la route synchrone ci-dessus.
   */
  async createModuleSpecTranslationJob(dossierId: string, version: number): Promise<ModuleSpecTranslationJob> {
    return sendExactJson<ModuleSpecTranslationJob>('POST', `${BASE_SPEC}/dossiers/${encodeURIComponent(dossierId)}/versions/${encodeURIComponent(String(version))}/translation-jobs`, undefined);
  }
  async listModuleSpecTranslationJobs(dossierId: string, limit = 5): Promise<ModuleSpecTranslationJob[]> {
    const response = await BaseApi.get(`${BASE_SPEC}/dossiers/${encodeURIComponent(dossierId)}/translation-jobs?limit=${encodeURIComponent(String(limit))}`);
    return response.data as ModuleSpecTranslationJob[];
  }
  async getModuleSpecTranslationJob(dossierId: string, jobId: string): Promise<ModuleSpecTranslationJob> {
    const response = await BaseApi.get(`${BASE_SPEC}/dossiers/${encodeURIComponent(dossierId)}/translation-jobs/${encodeURIComponent(jobId)}`);
    return response.data as ModuleSpecTranslationJob;
  }
  async cancelModuleSpecTranslationJob(dossierId: string, jobId: string): Promise<ModuleSpecTranslationJob> {
    return sendExactJson<ModuleSpecTranslationJob>('POST', `${BASE_SPEC}/dossiers/${encodeURIComponent(dossierId)}/translation-jobs/${encodeURIComponent(jobId)}/cancel`, undefined);
  }
  async resumeModuleSpecTranslationJob(dossierId: string, jobId: string): Promise<ModuleSpecTranslationJob> {
    return sendExactJson<ModuleSpecTranslationJob>('POST', `${BASE_SPEC}/dossiers/${encodeURIComponent(dossierId)}/translation-jobs/${encodeURIComponent(jobId)}/resume`, undefined);
  }

  async getDeliveryCheck(dossierId: string, version: number): Promise<DeliveryCheck> {
    const response = await BaseApi.get(
      `${BASE_SPEC}/dossiers/${encodeURIComponent(dossierId)}/versions/${encodeURIComponent(String(version))}/delivery-check`
    );
    return response.data as DeliveryCheck;
  }

  /* ── Habillage du module (UPT-2079 L14a, web §4) ─────────────────────── */

  async listReferenceCaptures(dossierId: string): Promise<ReferenceCapture[]> {
    return (await BaseApi.get(`${BASE}/dossiers/${encodeURIComponent(dossierId)}/captures`)).data as ReferenceCapture[];
  }
  async uploadReferenceCapture(dossierId: string, image: Blob, metadata: ReferenceCaptureInput): Promise<ReferenceCapture> {
    const fd = new FormData(); fd.append('image',image,'reference.jpg');
    fd.append('metadata',new Blob([JSON.stringify(metadata)],{type:'application/json'}));
    return (await BaseApi.postMultipart(`${BASE}/dossiers/${encodeURIComponent(dossierId)}/captures`,fd)).data as ReferenceCapture;
  }
  getReferenceCaptureBytes(dossierId: string, sha256: string, thumb = true): Promise<ArrayBuffer> {
    return getArrayBuffer(`${BASE}/dossiers/${encodeURIComponent(dossierId)}/captures/${encodeURIComponent(sha256)}/${thumb ? 'thumb' : 'content'}`);
  }
  async deleteReferenceCapture(dossierId: string, sha256: string): Promise<void> {
    await BaseApi.delete(`${BASE}/dossiers/${encodeURIComponent(dossierId)}/captures/${encodeURIComponent(sha256)}`);
  }
  async listModuleAssets(dossierId: string): Promise<ModuleAsset[]> {
    const response = await BaseApi.get(`${BASE}/dossiers/${encodeURIComponent(dossierId)}/assets`);
    return response.data as ModuleAsset[];
  }

  /** Multipart : partie `image` (octets ré-encodés par le navigateur) et partie `metadata` (JSON, `rightsAttested: true`). */
  async uploadModuleAsset(dossierId: string, image: Blob, metadata: ModuleAssetInput): Promise<ModuleAsset> {
    const fd = new FormData();
    fd.append('image', image, 'image');
    fd.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
    const response = await BaseApi.postMultipart(`${BASE}/dossiers/${encodeURIComponent(dossierId)}/assets`, fd);
    return response.data as ModuleAsset;
  }

  async copyModuleAssetFromPiece(dossierId: string, input: ModuleAssetFromPieceInput): Promise<ModuleAsset> {
    return sendExactJson<ModuleAsset>(
      'POST',
      `${BASE}/dossiers/${encodeURIComponent(dossierId)}/assets/from-piece`,
      { importId: input.importId, pieceId: input.pieceId }
    );
  }

  async getModuleAssetBytes(dossierId: string, sha256: string): Promise<ArrayBuffer> {
    return getArrayBuffer(
      `${BASE}/dossiers/${encodeURIComponent(dossierId)}/assets/${encodeURIComponent(sha256)}/content`
    );
  }

  async deleteModuleAsset(dossierId: string, sha256: string): Promise<void> {
    await BaseApi.delete(`${BASE}/dossiers/${encodeURIComponent(dossierId)}/assets/${encodeURIComponent(sha256)}`);
  }

  async getPrototypePresentation(importId: string): Promise<PrototypePresentationResponse> {
    const response = await BaseApi.get(`${BASE}/prototype-imports/${encodeURIComponent(importId)}/presentation`);
    return response.data as PrototypePresentationResponse;
  }

  /** Le document est envoyé EXACTEMENT (`sendExactJson`) : aucun champ de contexte SaaS ne doit entrer dans le Module Spec. */
  async validateModuleSpec(spec: ModuleSpecDocument): Promise<ValidationReport> {
    return sendExactJson<ValidationReport>('POST', `${BASE_SPEC}/validate`, spec);
  }

  /** Texte envoyé tel quel avec `Content-Type: application/json` ou `application/yaml` (jamais interprété côté web). */
  async validateModuleSpecText(text: string, format: 'json' | 'yaml'): Promise<ValidationReport> {
    const options = await BaseApi.getOptions(undefined);
    const response = await axios.request<ValidationReport>({
      method: 'POST',
      url: apiAzureUrl() + `${BASE_SPEC}/validate`,
      data: text,
      headers: { ...options.headers, 'Content-Type': format === 'yaml' ? 'application/yaml' : 'application/json' },
    });
    return response.data;
  }

  /* ── Concepteur (UPT-2079 L3, web §4.2) ───────────────────────────────── */

  /** 404 modulespec.draft.not_found → null (modèle `getSpec`) ; tout autre 404 → rejet. */
  async getModuleSpecDraft(dossierId: string): Promise<ModuleSpecDraft | null> {
    try {
      const response = await BaseApi.get(`${BASE_SPEC}/dossiers/${encodeURIComponent(dossierId)}/draft`);
      return (response.data as ModuleSpecDraft) ?? null;
    } catch (error) {
      if (isNotFound(error, 'modulespec.draft.not_found')) {
        return null;
      }
      throw error;
    }
  }

  /** `BaseApi.put` : le contexte SaaS reste à la racine du corps, hors de `spec` (rest §4.1) — jamais `sendExactJson`. */
  async putModuleSpecDraft(dossierId: string, input: ModuleSpecDraftInput): Promise<ModuleSpecDraft> { return executeStudioCommand(dossierId, 'save-draft', { input }); }

  async createDraftFromVersion(dossierId: string, version: number, archiveChecksum?: string): Promise<ModuleSpecDraft> { return executeStudioCommand(dossierId, 'save-draft', { fromVersion: version, ...(archiveChecksum ? { archiveChecksum } : {}) }); }

  /** Corps `{}` si `comment` et `feedbackIds` sont absents ; L10b : `feedbackIds` (retours `module_spec` corrigés par cette version) n'est envoyé que s'il n'est pas vide. */
  async proposeModuleSpecDraft(dossierId: string, input: ModuleSpecDraftProposeInput): Promise<ModuleSpecVersion> {
    return executeStudioCommand(dossierId, 'propose', { input });
  }

  async deleteModuleSpecDraft(dossierId: string): Promise<void> {
    await executeStudioCommand(dossierId, 'delete-draft', {});
  }

  async exportModuleToDraft(dossierId: string, moduleCode: string): Promise<ModuleSpecExportResult> { return executeStudioCommand(dossierId, 'save-draft', { moduleCode }); }

  async previewModuleExport(moduleCode: string): Promise<ModuleSpecExportPreview> {
    const response = await BaseApi.get(`${BASE_SPEC}/export/${encodeURIComponent(moduleCode)}`);
    return response.data as ModuleSpecExportPreview;
  }

  /** Mémoire de module dix minutes ; forme normalisée (tableaux absents → `[]`). */
  async getDesignerCatalog(): Promise<DesignerCatalog> {
    const now = Date.now();
    if (catalogCache && now - catalogCache.at < CATALOG_CACHE_MS) {
      return catalogCache.value;
    }
    const response = await BaseApi.get(`${BASE_SPEC}/catalog`);
    const value = normalizeCatalog(response.data);
    catalogCache = { at: now, value };
    return value;
  }

  /* ── Preview (UPT-2079 L4, web §4.2) : `encodeURIComponent` sur chaque identifiant ─────────────────── */

  async listPreviewDatasets(dossierId: string): Promise<PreviewDatasetSummary[]> {
    const response = await BaseApi.get(previewBase(dossierId));
    return response.data as PreviewDatasetSummary[];
  }

  /** 404 preview.dataset.not_found → null (modèle `getSpec`) ; tout autre 404 → rejet. */
  async getPreviewDataset(dossierId: string, datasetId: string): Promise<PreviewDataset | null> {
    try {
      const response = await BaseApi.get(previewPath(dossierId, datasetId));
      return (response.data as PreviewDataset) ?? null;
    } catch (error) {
      if (isNotFound(error, 'preview.dataset.not_found')) {
        return null;
      }
      throw error;
    }
  }

  async createPreviewDataset(dossierId: string, input: PreviewDatasetCreateInput): Promise<PreviewDataset> {
    const response = await BaseApi.post(previewBase(dossierId), input);
    return response.data as PreviewDataset;
  }

  /** `BaseApi.put` : le contexte SaaS reste à la racine du corps, hors de `rows` (le serveur ignore les champs inconnus) ; pas de `sendExactJson`. */
  async updatePreviewDataset(
    dossierId: string,
    datasetId: string,
    input: PreviewDatasetUpdateInput
  ): Promise<PreviewDataset> {
    const response = await BaseApi.put(previewPath(dossierId, datasetId), input);
    return response.data as PreviewDataset;
  }

  async generatePreviewRows(dossierId: string, datasetId: string, input: PreviewGenerateInput): Promise<PreviewDataset> {
    const response = await BaseApi.post(`${previewPath(dossierId, datasetId)}/generate`, input);
    return response.data as PreviewDataset;
  }

  async refreshPreviewSnapshot(
    dossierId: string,
    datasetId: string,
    input: PreviewRefreshInput
  ): Promise<PreviewRefreshResult> {
    const response = await BaseApi.post(`${previewPath(dossierId, datasetId)}/refresh`, input);
    return response.data as PreviewRefreshResult;
  }

  async acknowledgePreviewWarnings(
    dossierId: string,
    datasetId: string,
    input: PreviewAcknowledgeInput
  ): Promise<PreviewDataset> {
    const response = await BaseApi.post(`${previewPath(dossierId, datasetId)}/acknowledge`, input);
    return response.data as PreviewDataset;
  }

  async deletePreviewDataset(dossierId: string, datasetId: string): Promise<void> {
    await BaseApi.delete(previewPath(dossierId, datasetId));
  }

  async createPreviewShare(dossierId: string, datasetId: string, input: PreviewShareInput): Promise<PreviewShareCreated> {
    const response = await BaseApi.post(`${previewPath(dossierId, datasetId)}/share`, input);
    return response.data as PreviewShareCreated;
  }

  async revokePreviewShare(dossierId: string, datasetId: string): Promise<void> {
    await BaseApi.delete(`${previewPath(dossierId, datasetId)}/share`);
  }

  /* ── Générateur (UPT-2079 L5, web §4.2) : `encodeURIComponent` sur chaque identifiant ──────────────── */

  async listGenerationRuns(dossierId: string): Promise<GeneratorRunSummary[]> {
    const response = await BaseApi.get(generatorBase(dossierId));
    return response.data as GeneratorRunSummary[];
  }

  /** 404 generator.run.not_found → null (modèle `getSpec`) ; tout autre 404 → rejet. */
  async getGenerationRun(dossierId: string, runId: string): Promise<GeneratorRun | null> {
    try {
      const response = await BaseApi.get(generatorPath(dossierId, runId));
      return (response.data as GeneratorRun) ?? null;
    } catch (error) {
      if (isNotFound(error, 'generator.run.not_found')) {
        return null;
      }
      throw error;
    }
  }

  /** GAP-60 : avec le dossier, la date proposée dépasse la dernière génération réussie de son module (relivraison sans correction). */
  async suggestedMigrationDate(dossierId?: string): Promise<string> {
    const response = await BaseApi.get(`${BASE}/generation-migration-date` + (dossierId ? '?dossier=' + encodeURIComponent(dossierId) : ''));
    return response.data.migrationDate;
  }

  /** Le serveur répond 201 même quand le plan a des erreurs. */
  async createGenerationRun(dossierId: string, input: GeneratorBundleInput): Promise<GeneratorRun> {
    return executeStudioCommand(dossierId, 'deliver', { input });
  }

  async deleteGenerationRun(dossierId: string, runId: string): Promise<void> {
    await BaseApi.delete(generatorPath(dossierId, runId));
  }

  /** `?path=` doit être égal à un chemin du plan ; le contenu est du texte UTF-8, jamais relu comme JSON. */
  async readGeneratorFile(dossierId: string, runId: string, path: string): Promise<string> {
    const content = await getGeneratorContent<unknown>(`${generatorPath(dossierId, runId)}/files`, 'text', { path });
    return typeof content === 'string' ? content : '';
  }

  async downloadGeneratorBundle(dossierId: string, runId: string, installation = false): Promise<Blob> {
    if (installation) { const response = await BaseApi.postExcel('/api/v1/module-studio/dossiers/' + encodeURIComponent(dossierId) + '/commands/install', { confirmed: true, parameters: { runId }, requestId: studioRequestId() }); return response.data as Blob; }
    return getGeneratorContent<Blob>(`${generatorPath(dossierId, runId)}/bundle`, 'blob');
  }

  /** L5c : `apply: false` simule (aucune écriture) ; `apply: true` crée ou réutilise, puis écrit la ligne de catalogue. */
  async runGeneratorStripe(dossierId: string, input: GeneratorStripeInput): Promise<GeneratorStripeResult> {
    const response = await BaseApi.post(`${BASE}/dossiers/${encodeURIComponent(dossierId)}/stripe`, input);
    return response.data as GeneratorStripeResult;
  }

  /**
   * L5d : dépose l'archive dans le dépôt git du module. La route ignore tout corps (`BaseApi.post` y ajoute de toute façon le
   * contexte du locataire) ; la réponse 200 est lue telle quelle. Les erreurs 409 / 502 passent par `readDesignError`.
   */
  async depositGeneratorRun(dossierId: string, runId: string, options?: { createRepository?: boolean; openPullRequest?: boolean; suffix?: string }): Promise<GeneratorRepoDeposit> {
    return executeStudioCommand(dossierId, 'deposit', { runId, ...(options ?? {}) });
  }

  /* ── Pipeline CODE > DATA (UPT-2079 L6a, web §4.2) : lecture seule côté Azure DevOps, aucun déclenchement ─── */

  async getPipelineEnvironments(): Promise<PipelineEnvironmentsPayload> {
    const response = await BaseApi.get(`${BASE}/pipeline/environments`);
    return response.data as PipelineEnvironmentsPayload;
  }

  async updatePipelineEnvironment(code: EnvironmentCode, input: PipelineEnvironmentInput): Promise<PipelineEnvironment> {
    const response = await BaseApi.put(pipelineEnvironmentPath(code), input);
    return response.data as PipelineEnvironment;
  }

  /** Hors `BASE` : `GET /api/tenants/options` (liste nue ou enveloppée dans `data`). */
  async listPipelineTenants(): Promise<PipelineTenantOption[]> {
    const response = await BaseApi.get(TENANT_OPTIONS_PATH);
    return parsePipelineTenantOptions(response.data);
  }

  /** Les filtres absents ne sont pas envoyés ; `limit` part en chaîne. */
  async listPipelineRuns(filter?: {
    kind?: PipelineKind;
    env?: EnvironmentCode;
    moduleCode?: string;
    limit?: number;
  }): Promise<PipelineRunSummary[]> {
    const params: Record<string, string> = {};
    if (filter?.kind) params.kind = filter.kind;
    if (filter?.env) params.env = filter.env;
    if (filter?.moduleCode) params.moduleCode = filter.moduleCode;
    if (typeof filter?.limit === 'number') params.limit = String(filter.limit);
    const response =
      Object.keys(params).length > 0
        ? await BaseApi.get(`${BASE}/pipeline/runs`, params)
        : await BaseApi.get(`${BASE}/pipeline/runs`);
    return response.data as PipelineRunSummary[];
  }

  /** 404 pipeline.run.not_found → null (modèle `getSpec`) ; tout autre 404 → rejet. */
  async getPipelineRun(id: string): Promise<PipelineRun | null> {
    try {
      const response = await BaseApi.get(pipelineRunPath(id));
      return (response.data as PipelineRun) ?? null;
    } catch (error) {
      if (isNotFound(error, 'pipeline.run.not_found')) {
        return null;
      }
      throw error;
    }
  }

  /** Un relevé CODE : le serveur répond 201 MÊME quand la lecture a échoué, le statut `failed` se lit dans le corps. */
  async readPipelineEnvironment(env: EnvironmentCode): Promise<PipelineRun> {
    const response = await BaseApi.post(`${BASE}/pipeline/runs`, { kind: 'CODE', env });
    return response.data as PipelineRun;
  }

  async deletePipelineRun(id: string): Promise<void> {
    await BaseApi.delete(pipelineRunPath(id));
  }

  /* ── Copie de données (UPT-2079 L6b, web §4.2) : trois gestes, UN appel chacun ─── */

  /** Le serveur répond 201 `planned` MÊME quand le plan porte des erreurs (`data.issues`) : jamais déduit du code HTTP. Les champs absents ne sont pas envoyés. */
  async planPipelinePromotion(input: Omit<PipelineDataRunInput, 'kind'>): Promise<PipelineRun> {
    const response = await BaseApi.post(`${BASE}/pipeline/runs`, withoutUndefined({ kind: 'DATA', ...input }));
    return response.data as PipelineRun;
  }

  /** Corps `{planSha256}` : l'empreinte COMPLÈTE du plan lu par l'humain. */
  async executePipelineRun(id: string, input: PipelineExecuteInput): Promise<PipelineRun> {
    const response = await BaseApi.post(`${pipelineRunPath(id)}/execute`, { planSha256: input.planSha256 });
    return response.data as PipelineRun;
  }

  /** Sans corps (comme `depositGeneratorRun`). */
  async rollbackPipelineRun(id: string): Promise<PipelineRun> {
    const response = await BaseApi.post(`${pipelineRunPath(id)}/rollback`, {});
    return response.data as PipelineRun;
  }

  /* ── Conditions avant la copie, approbation, parcours (UPT-2079 L6c, web §4.2) : UN appel chacun ─── */

  /** Corps `{planSha256}` : l'empreinte COMPLÈTE du plan que l'approbateur a lu. Un 403 (droit `ALL` manquant) est lu par `readDesignError`. */
  async approvePipelineRun(id: string, input: PipelineApproveInput): Promise<PipelineRun> {
    const response = await BaseApi.post(`${pipelineRunPath(id)}/approve`, { planSha256: input.planSha256 });
    return response.data as PipelineRun;
  }

  /** Condition « tests réussis » de l'environnement `env` (paramètre de requête `env`). */
  async getPipelineGates(moduleCode: string, env: EnvironmentCode): Promise<PipelineGateTests> {
    const response = await BaseApi.get(`${pipelineModulePath(moduleCode)}/gates`, { env });
    return response.data as PipelineGateTests;
  }

  async getPipelineJourney(moduleCode: string): Promise<PipelineJourney> {
    const response = await BaseApi.get(`${pipelineModulePath(moduleCode)}/journey`);
    return response.data as PipelineJourney;
  }

  /** Sans corps : la licence que poserait un administrateur pour le locataire de l'environnement, sans rien publier. */
  async openPipelineEnvironment(moduleCode: string, env: EnvironmentCode): Promise<PipelineLicenseResult> {
    const response = await BaseApi.post(`${pipelineModulePath(moduleCode)}/environments/${encodeURIComponent(env)}/license`, {});
    return response.data as PipelineLicenseResult;
  }

  /* ── Retours de recette classés (UPT-2079 L10a, web §4.2) ─────────────────────────────────────────────────── */

  /** Filtres absents non envoyés ; `status` et `category` jointes par des virgules (`category=none` = pas encore classé) ; `limit` et `offset` partent en chaîne. */
  async listFeedback(filter?: FeedbackFilter): Promise<FeedbackPage> {
    const params: Record<string, string> = {};
    if (filter?.dossierId) params.dossierId = filter.dossierId;
    if (filter?.moduleCode) params.moduleCode = filter.moduleCode;
    if (filter?.runId) params.runId = filter.runId;
    if (filter?.stepId) params.stepId = filter.stepId;
    if (filter?.status && filter.status.length > 0) params.status = filter.status.join(',');
    if (filter?.category && filter.category.length > 0) params.category = filter.category.join(',');
    if (filter?.routedTo) params.routedTo = filter.routedTo;
    if (typeof filter?.limit === 'number') params.limit = String(filter.limit);
    if (typeof filter?.offset === 'number') params.offset = String(filter.offset);
    const response =
      Object.keys(params).length > 0 ? await BaseApi.get(FEEDBACK_BASE, params) : await BaseApi.get(FEEDBACK_BASE);
    return response.data as FeedbackPage;
  }

  /** 404 feedback.not_found → null (modèle `getSpec`) ; tout autre 404 → rejet. */
  async getFeedback(id: string): Promise<Feedback | null> {
    try {
      const response = await BaseApi.get(feedbackPath(id));
      return (response.data as Feedback) ?? null;
    } catch (error) {
      if (isNotFound(error, 'feedback.not_found')) {
        return null;
      }
      throw error;
    }
  }

  async createFeedback(input: FeedbackInput): Promise<Feedback> {
    const response = await BaseApi.post(FEEDBACK_BASE, withoutUndefined(input));
    return response.data as Feedback;
  }

  async updateFeedback(id: string, input: FeedbackUpdateInput): Promise<Feedback> {
    const response = await BaseApi.put(feedbackPath(id), withoutUndefined(input));
    return response.data as Feedback;
  }

  async classifyFeedback(input: FeedbackClassifyInput): Promise<FeedbackSummary[]> {
    const response = await BaseApi.post(`${FEEDBACK_BASE}/classify`, withoutUndefined(input));
    return response.data as FeedbackSummary[];
  }

  async transmitFeedback(input: FeedbackTransmitInput): Promise<FeedbackSummary[]> {
    const response = await BaseApi.post(`${FEEDBACK_BASE}/transmit`, withoutUndefined(input));
    return response.data as FeedbackSummary[];
  }

  /** Un `POST` en LECTURE (droit READ côté serveur) : aucune écriture. */
  async getFeedbackTransmission(input: FeedbackTransmissionInput): Promise<FeedbackTransmission> {
    const response = await BaseApi.post(`${FEEDBACK_BASE}/transmission`, withoutUndefined(input));
    return response.data as FeedbackTransmission;
  }

  async resolveFeedback(input: FeedbackResolveInput): Promise<FeedbackSummary[]> {
    const response = await BaseApi.post(`${FEEDBACK_BASE}/resolve`, withoutUndefined(input));
    return response.data as FeedbackSummary[];
  }

  async rejectFeedback(input: FeedbackRejectInput): Promise<FeedbackSummary[]> {
    const response = await BaseApi.post(`${FEEDBACK_BASE}/reject`, withoutUndefined(input));
    return response.data as FeedbackSummary[];
  }

  async reopenFeedback(input: FeedbackReopenInput): Promise<FeedbackSummary[]> {
    const response = await BaseApi.post(`${FEEDBACK_BASE}/reopen`, withoutUndefined(input));
    return response.data as FeedbackSummary[];
  }
}

/** Dépôt REST qui refuse tout appel (ressource non servie) — filet de sécurité. */
export class NotServedRepository implements ModuleStudioRepository {
  getFaithfulState: ModuleStudioRepository['getFaithfulState'] = () => fail(this.resource, 'getFaithfulState');
  putFaithfulState: ModuleStudioRepository['putFaithfulState'] = () => fail(this.resource, 'putFaithfulState');
  getTranslationPreference(): Promise<TranslationPreference> { return fail(this.resource, 'getTranslationPreference'); }
  setTranslationPreference(): Promise<TranslationPreference> { return fail(this.resource, 'setTranslationPreference'); }
  createStarterTestPlan(): Promise<StarterTestPlanResult> { return fail(this.resource, 'createStarterTestPlan'); }
  listReferenceCaptures(): Promise<ReferenceCapture[]> { return fail(this.resource, 'listReferenceCaptures'); }
  uploadReferenceCapture(): Promise<ReferenceCapture> { return fail(this.resource, 'uploadReferenceCapture'); }
  getReferenceCaptureBytes(): Promise<ArrayBuffer> { return fail(this.resource, 'getReferenceCaptureBytes'); }
  deleteReferenceCapture(): Promise<void> { return fail(this.resource, 'deleteReferenceCapture'); }
  listSimulations(): Promise<SimulationSummary[]> { return fail(this.resource, 'listSimulations'); }
  getSimulation(): Promise<Simulation | null> { return fail(this.resource, 'getSimulation'); }
  createSimulation(): Promise<Simulation> { return fail(this.resource, 'createSimulation'); }
  updateSimulation(): Promise<Simulation> { return fail(this.resource, 'updateSimulation'); }
  generateSimulationData(): Promise<Simulation> { return fail(this.resource, 'generateSimulationData'); }
  refreshSimulation(): Promise<SimulationRefreshResult> { return fail(this.resource, 'refreshSimulation'); }
  acknowledgeSimulationWarnings(): Promise<Simulation> { return fail(this.resource, 'acknowledgeSimulationWarnings'); }
  deleteSimulation(): Promise<void> { return fail(this.resource, 'deleteSimulation'); }
  createSimulationShare(): Promise<SimulationShareCreated> { return fail(this.resource, 'createSimulationShare'); }
  revokeSimulationShare(): Promise<void> { return fail(this.resource, 'revokeSimulationShare'); }

  listIdeas(): Promise<Idea[]> { return fail(this.resource, 'listIdeas'); }
  createIdea(): Promise<Idea> { return fail(this.resource, 'createIdea'); }
  getIdea(): Promise<Idea> { return fail(this.resource, 'getIdea'); }
  updateIdea(): Promise<Idea> { return fail(this.resource, 'updateIdea'); }
  ideaEstimate(): Promise<IdeaEstimate> { return fail(this.resource, 'ideaEstimate'); }
  fetchIdeaRepository(): Promise<Idea> { return fail(this.resource, 'fetchIdeaRepository'); }
  getIdeaRepository(): Promise<IdeaRepositoryTree> { return fail(this.resource, 'getIdeaRepository'); }
  startBrief(): Promise<Idea> { return fail(this.resource, 'startBrief'); }
  acceptBrief(): Promise<Idea> { return fail(this.resource, 'acceptBrief'); }
  startPrototype(): Promise<Idea> { return fail(this.resource, 'startPrototype'); }
  getPrototypeHtml(): Promise<string> { return fail(this.resource, 'getPrototypeHtml'); }
  getIdeaPrompt(): Promise<string> { return fail(this.resource, 'getIdeaPrompt'); }
  cancelIdea(): Promise<Idea> { return fail(this.resource, 'cancelIdea'); }
  transferIdea(): Promise<IdeaTransfer> { return fail(this.resource, 'transferIdea'); }
  deleteIdea(): Promise<void> { return fail(this.resource, 'deleteIdea'); }

  constructor(private readonly resource: ModuleStudioResource) {}

  listDossiers(): Promise<DesignDossier[]> { return fail(this.resource, 'listDossiers'); }
  getDossier(): Promise<DesignDossier | null> { return fail(this.resource, 'getDossier'); }
  saveDossier(): Promise<DesignDossier> { return fail(this.resource, 'saveDossier'); }
  deleteDossier(): Promise<void> { return fail(this.resource, 'deleteDossier'); }
  getModuleSpec(): Promise<DesignModuleSpecPayload> { return fail(this.resource, 'getModuleSpec'); }
  putModuleSpec(): Promise<DesignDossier> { return fail(this.resource, 'putModuleSpec'); }
  listSpecs(): Promise<SpecDocument[]> { return fail(this.resource, 'listSpecs'); }
  getSpec(): Promise<DesignSpecDetail | null> { return fail(this.resource, 'getSpec'); }
  createSpec(): Promise<DesignSpecDetail> { return fail(this.resource, 'createSpec'); }
  updateSpec(): Promise<DesignSpecDetail> { return fail(this.resource, 'updateSpec'); }
  listSpecVersions(): Promise<DesignSpecVersionSummary[]> { return fail(this.resource, 'listSpecVersions'); }
  getSpecVersion(): Promise<DesignSpecVersion> { return fail(this.resource, 'getSpecVersion'); }
  diffSpec(): Promise<DesignSpecDiff> { return fail(this.resource, 'diffSpec'); }
  generateSpec(): Promise<DesignSpecDetail> { return fail(this.resource, 'generateSpec'); }
  syncSpec(): Promise<DesignSpecSyncResult> { return fail(this.resource, 'syncSpec'); }
  listTestPlans(): Promise<TestPlanSummary[]> { return fail(this.resource, 'listTestPlans'); }
  getTestPlan(): Promise<TestPlan | null> { return fail(this.resource, 'getTestPlan'); }
  saveTestPlan(): Promise<TestPlan> { return fail(this.resource, 'saveTestPlan'); }
  listTestRuns(): Promise<TestRunSummary[]> { return fail(this.resource, 'listTestRuns'); }
  getTestRun(): Promise<TestRun | null> { return fail(this.resource, 'getTestRun'); }
  createTestRun(): Promise<TestRun> { return fail(this.resource, 'createTestRun'); }
  putTestResult(): Promise<TestResult> { return fail(this.resource, 'putTestResult'); }
  clearTestResult(): Promise<void> { return fail(this.resource, 'clearTestResult'); }
  validateProposedResult(): Promise<TestResult> { return fail(this.resource, 'validateProposedResult'); }
  putSceneNote(): Promise<void> { return fail(this.resource, 'putSceneNote'); }
  closeTestRun(): Promise<TestRun> { return fail(this.resource, 'closeTestRun'); }
  listPrototypeImports(): Promise<PrototypeImportSummary[]> {
    return fail(this.resource, 'listPrototypeImports');
  }
  getPrototypeImport(): Promise<PrototypeImport> { return fail(this.resource, 'getPrototypeImport'); }
  createPrototypeImport(): Promise<PrototypeImport> { return fail(this.resource, 'createPrototypeImport'); }
  addPrototypeSource(): Promise<PrototypeImport> { return fail(this.resource, 'addPrototypeSource'); }
  addPrototypeImage(): Promise<PrototypeImport> { return fail(this.resource, 'addPrototypeImage'); }
  getPrototypePrompt(): Promise<PrototypePrompt | null> { return fail(this.resource, 'getPrototypePrompt'); }
  getPrototypePieceText(): Promise<string> { return fail(this.resource, 'getPrototypePieceText'); }
  getPrototypePieceImage(): Promise<Blob> { return fail(this.resource, 'getPrototypePieceImage'); }
  deletePrototypeSnapshot(): Promise<PrototypeImport> { return fail(this.resource, 'deletePrototypeSnapshot'); }
  addPrototypeSnapshot(): Promise<PrototypeSnapshotResult> {
    return fail(this.resource, 'addPrototypeSnapshot');
  }
  putPrototypeInventory(): Promise<PrototypeImport> { return fail(this.resource, 'putPrototypeInventory'); }
  getModuleComponents(): Promise<ModuleComponents> { return fail(this.resource, 'getModuleComponents'); }
  refreshModuleComponents(): Promise<ModuleComponents> { return fail(this.resource, 'refreshModuleComponents'); }
  proposeBindings(): Promise<BindingCandidate[][]> { return fail(this.resource, 'proposeBindings'); }
  getCapabilityCatalog(): Promise<CapabilityCatalog> { return fail(this.resource, 'getCapabilityCatalog'); }
  getPrototypeCoverage(): Promise<CoverageReport> { return fail(this.resource, 'getPrototypeCoverage'); }
  getGapReport(): Promise<GapReport> { return fail(this.resource, 'getGapReport'); }
  getSpecCoverage(): Promise<CoverageReport> { return fail(this.resource, 'getSpecCoverage'); }
  getNavigationProposal(): Promise<NavigationProposal> { return fail(this.resource, 'getNavigationProposal'); }
  getPrototypeInventory(): Promise<PrototypeInventory> { return fail(this.resource, 'getPrototypeInventory'); }
  patchPrototypeImport(): Promise<PrototypeImport> { return fail(this.resource, 'patchPrototypeImport'); }
  deletePrototypeImport(): Promise<void> { return fail(this.resource, 'deletePrototypeImport'); }
  prototypeRemovalImpact(): ReturnType<ModuleStudioRepository['prototypeRemovalImpact']> { return fail(this.resource, 'prototypeRemovalImpact'); }
  restorePrototypeImport(): Promise<void> { return fail(this.resource, 'restorePrototypeImport'); }
  hidePrototypeImport(): Promise<void> { return fail(this.resource, 'hidePrototypeImport'); }
  acknowledgePrototypeWarnings(): Promise<PrototypeImport> {
    return fail(this.resource, 'acknowledgePrototypeWarnings');
  }
  getPrototypeAnalysisEstimate(): Promise<PrototypeAnalysisEstimate> {
    return fail(this.resource, 'getPrototypeAnalysisEstimate');
  }
  getAiRoute(): Promise<AiRoute> { return fail(this.resource, 'getAiRoute'); }
  startPrototypeAnalysis(): Promise<PrototypeAnalysis> { return fail(this.resource, 'startPrototypeAnalysis'); }
  listPrototypeAnalyses(): Promise<PrototypeAnalysisSummary[]> {
    return fail(this.resource, 'listPrototypeAnalyses');
  }
  getPrototypeAnalysis(): Promise<PrototypeAnalysis> { return fail(this.resource, 'getPrototypeAnalysis'); }
  applyPrototypeAnalysis(): Promise<PrototypeAnalysisApplyResult> {
    return fail(this.resource, 'applyPrototypeAnalysis');
  }
  rejectPrototypeAnalysis(): Promise<PrototypeAnalysis> { return fail(this.resource, 'rejectPrototypeAnalysis'); }
  listModuleSpecVersions(): Promise<ModuleSpecVersionSummary[]> { return fail(this.resource, 'listModuleSpecVersions'); }
  getModuleSpecVersion(): Promise<ModuleSpecVersion> { return fail(this.resource, 'getModuleSpecVersion'); }
  diffModuleSpecVersions(): Promise<ModuleSpecDiffSection[]> { return fail(this.resource, 'diffModuleSpecVersions'); }
  proposeModuleSpecVersion(): Promise<ModuleSpecVersion> { return fail(this.resource, 'proposeModuleSpecVersion'); }
  decideModuleSpecVersion(): Promise<ModuleSpecVersion> { return fail(this.resource, 'decideModuleSpecVersion'); }
  previewModuleSpecRepair(): Promise<ModuleSpecRepairPreview> { return fail(this.resource, 'previewModuleSpecRepair'); }
  previewRelations():Promise<RelationsResult>{return fail(this.resource,'previewRelations');}
  applyRelations():Promise<RelationsResult>{return fail(this.resource,'applyRelations');}
  repairModuleSpecVersion(): Promise<ModuleSpecVersion> { return fail(this.resource, 'repairModuleSpecVersion'); }
  getDeliveryCheck(): Promise<DeliveryCheck> { return fail(this.resource, 'getDeliveryCheck'); }
  estimateModuleSpecTranslation(): Promise<ModuleSpecTranslationEstimate> { return fail(this.resource, 'estimateModuleSpecTranslation'); }
  translateModuleSpecVersion(): Promise<ModuleSpecVersion> { return fail(this.resource, 'translateModuleSpecVersion'); }
  createModuleSpecTranslationJob(): Promise<ModuleSpecTranslationJob> { return fail(this.resource, 'createModuleSpecTranslationJob'); }
  listModuleSpecTranslationJobs(): Promise<ModuleSpecTranslationJob[]> { return fail(this.resource, 'listModuleSpecTranslationJobs'); }
  getModuleSpecTranslationJob(): Promise<ModuleSpecTranslationJob> { return fail(this.resource, 'getModuleSpecTranslationJob'); }
  cancelModuleSpecTranslationJob(): Promise<ModuleSpecTranslationJob> { return fail(this.resource, 'cancelModuleSpecTranslationJob'); }
  resumeModuleSpecTranslationJob(): Promise<ModuleSpecTranslationJob> { return fail(this.resource, 'resumeModuleSpecTranslationJob'); }
  listModuleAssets(): Promise<ModuleAsset[]> { return fail(this.resource, 'listModuleAssets'); }
  uploadModuleAsset(): Promise<ModuleAsset> { return fail(this.resource, 'uploadModuleAsset'); }
  copyModuleAssetFromPiece(): Promise<ModuleAsset> { return fail(this.resource, 'copyModuleAssetFromPiece'); }
  getModuleAssetBytes(): Promise<ArrayBuffer> { return fail(this.resource, 'getModuleAssetBytes'); }
  deleteModuleAsset(): Promise<void> { return fail(this.resource, 'deleteModuleAsset'); }
  getPrototypePresentation(): Promise<PrototypePresentationResponse> { return fail(this.resource, 'getPrototypePresentation'); }
  validateModuleSpec(): Promise<ValidationReport> { return fail(this.resource, 'validateModuleSpec'); }
  validateModuleSpecText(): Promise<ValidationReport> { return fail(this.resource, 'validateModuleSpecText'); }
  getModuleSpecDraft(): Promise<ModuleSpecDraft | null> { return fail(this.resource, 'getModuleSpecDraft'); }
  putModuleSpecDraft(): Promise<ModuleSpecDraft> { return fail(this.resource, 'putModuleSpecDraft'); }
  getDossierThread(): Promise<DossierThread> { return fail(this.resource, 'getDossierThread'); }
  createDraftFromVersion(): Promise<ModuleSpecDraft> { return fail(this.resource, 'createDraftFromVersion'); }
  proposeModuleSpecDraft(): Promise<ModuleSpecVersion> { return fail(this.resource, 'proposeModuleSpecDraft'); }
  deleteModuleSpecDraft(): Promise<void> { return fail(this.resource, 'deleteModuleSpecDraft'); }
  exportModuleToDraft(): Promise<ModuleSpecExportResult> { return fail(this.resource, 'exportModuleToDraft'); }
  previewModuleExport(): Promise<ModuleSpecExportPreview> { return fail(this.resource, 'previewModuleExport'); }
  getDesignerCatalog(): Promise<DesignerCatalog> { return fail(this.resource, 'getDesignerCatalog'); }
  listPreviewDatasets(): Promise<PreviewDatasetSummary[]> { return fail(this.resource, 'listPreviewDatasets'); }
  getPreviewDataset(): Promise<PreviewDataset | null> { return fail(this.resource, 'getPreviewDataset'); }
  createPreviewDataset(): Promise<PreviewDataset> { return fail(this.resource, 'createPreviewDataset'); }
  updatePreviewDataset(): Promise<PreviewDataset> { return fail(this.resource, 'updatePreviewDataset'); }
  generatePreviewRows(): Promise<PreviewDataset> { return fail(this.resource, 'generatePreviewRows'); }
  refreshPreviewSnapshot(): Promise<PreviewRefreshResult> { return fail(this.resource, 'refreshPreviewSnapshot'); }
  acknowledgePreviewWarnings(): Promise<PreviewDataset> { return fail(this.resource, 'acknowledgePreviewWarnings'); }
  deletePreviewDataset(): Promise<void> { return fail(this.resource, 'deletePreviewDataset'); }
  createPreviewShare(): Promise<PreviewShareCreated> { return fail(this.resource, 'createPreviewShare'); }
  revokePreviewShare(): Promise<void> { return fail(this.resource, 'revokePreviewShare'); }
  listGenerationRuns(): Promise<GeneratorRunSummary[]> { return fail(this.resource, 'listGenerationRuns'); }
  getGenerationRun(): Promise<GeneratorRun | null> { return fail(this.resource, 'getGenerationRun'); }
  suggestedMigrationDate(): Promise<string> { return fail(this.resource, 'suggestedMigrationDate'); }
  createGenerationRun(): Promise<GeneratorRun> { return fail(this.resource, 'createGenerationRun'); }
  deleteGenerationRun(): Promise<void> { return fail(this.resource, 'deleteGenerationRun'); }
  readGeneratorFile(): Promise<string> { return fail(this.resource, 'readGeneratorFile'); }
  downloadGeneratorBundle(): Promise<Blob> { return fail(this.resource, 'downloadGeneratorBundle'); }
  runGeneratorStripe(): Promise<GeneratorStripeResult> { return fail(this.resource, 'runGeneratorStripe'); }
  depositGeneratorRun(): Promise<GeneratorRepoDeposit> { return fail(this.resource, 'depositGeneratorRun'); }
  getPipelineEnvironments(): Promise<PipelineEnvironmentsPayload> { return fail(this.resource, 'getPipelineEnvironments'); }
  updatePipelineEnvironment(): Promise<PipelineEnvironment> { return fail(this.resource, 'updatePipelineEnvironment'); }
  listPipelineTenants(): Promise<PipelineTenantOption[]> { return fail(this.resource, 'listPipelineTenants'); }
  listPipelineRuns(): Promise<PipelineRunSummary[]> { return fail(this.resource, 'listPipelineRuns'); }
  getPipelineRun(): Promise<PipelineRun | null> { return fail(this.resource, 'getPipelineRun'); }
  readPipelineEnvironment(): Promise<PipelineRun> { return fail(this.resource, 'readPipelineEnvironment'); }
  deletePipelineRun(): Promise<void> { return fail(this.resource, 'deletePipelineRun'); }
  planPipelinePromotion(): Promise<PipelineRun> { return fail(this.resource, 'planPipelinePromotion'); }
  executePipelineRun(): Promise<PipelineRun> { return fail(this.resource, 'executePipelineRun'); }
  rollbackPipelineRun(): Promise<PipelineRun> { return fail(this.resource, 'rollbackPipelineRun'); }
  approvePipelineRun(): Promise<PipelineRun> { return fail(this.resource, 'approvePipelineRun'); }
  getPipelineGates(): Promise<PipelineGateTests> { return fail(this.resource, 'getPipelineGates'); }
  getPipelineJourney(): Promise<PipelineJourney> { return fail(this.resource, 'getPipelineJourney'); }
  openPipelineEnvironment(): Promise<PipelineLicenseResult> { return fail(this.resource, 'openPipelineEnvironment'); }
  listFeedback(): Promise<FeedbackPage> { return fail(this.resource, 'listFeedback'); }
  getFeedback(): Promise<Feedback | null> { return fail(this.resource, 'getFeedback'); }
  createFeedback(): Promise<Feedback> { return fail(this.resource, 'createFeedback'); }
  updateFeedback(): Promise<Feedback> { return fail(this.resource, 'updateFeedback'); }
  classifyFeedback(): Promise<FeedbackSummary[]> { return fail(this.resource, 'classifyFeedback'); }
  transmitFeedback(): Promise<FeedbackSummary[]> { return fail(this.resource, 'transmitFeedback'); }
  getFeedbackTransmission(): Promise<FeedbackTransmission> { return fail(this.resource, 'getFeedbackTransmission'); }
  resolveFeedback(): Promise<FeedbackSummary[]> { return fail(this.resource, 'resolveFeedback'); }
  rejectFeedback(): Promise<FeedbackSummary[]> { return fail(this.resource, 'rejectFeedback'); }
  reopenFeedback(): Promise<FeedbackSummary[]> { return fail(this.resource, 'reopenFeedback'); }
}
